import type { ReactNode } from 'react';
import { Accordion, AccordionItem, Button } from '@carbon/react';
import { OpenPanelFilledLeft, OpenPanelLeft } from '@carbon/react/icons';
import { format0 } from 'utils/number';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type { TableState } from 'components/data-table/types';
import type { Row } from '@tanstack/react-table';
import type {
  AddressFailure,
  AddressGroup,
  AddressGroupFilters as Filters,
  Run,
} from '../types';
import { PLAIN_TABLE_CONFIG } from '../shared';
import { AddressGroupFilters } from './address-group-filters';
import { failureColumns } from './address-run-helpers';
import styles from '../page.module.scss';

type Pagination = { page: number; pageSize: number };

interface AddressRunTablesProps {
  executed: boolean;
  failures: AddressFailure[];
  failuresTotal: number;
  filters: Filters;
  onFiltersChange: (patch: Partial<Filters>) => void;
  filtersOpen: boolean;
  onToggleFilters: () => void;
  groups: AddressGroup[];
  columns: DataTableColumnDef<AddressGroup>[];
  loading: boolean;
  total: number;
  pagination: Pagination;
  search: string;
  onPaginationChange: (next: Pagination) => void;
  onSearchChange: (search: string) => void;
  renderVariations: (row: Row<AddressGroup>) => ReactNode;
  executions: Run[];
  execColumns: DataTableColumnDef<Run>[];
}

/**
 * The run's three tables: the (post-execution) failed-deduplications panel, the
 * filterable group list with its expandable variations, and the executions log.
 * @param {AddressRunTablesProps} props The tables.
 * @return {JSX.Element} The tables.
 */
export function AddressRunTables({
  executed,
  failures,
  failuresTotal,
  filters,
  onFiltersChange,
  filtersOpen,
  onToggleFilters,
  groups,
  columns,
  loading,
  total,
  pagination,
  search,
  onPaginationChange,
  onSearchChange,
  renderVariations,
  executions,
  execColumns,
}: AddressRunTablesProps) {
  return (
    <>
      {executed && failures.length > 0 && (
        <div className={styles.failedAccordion}>
          <Accordion>
            <AccordionItem
              open
              title={`Failed Deduplications (${format0(failuresTotal)}${
                failuresTotal > failures.length
                  ? ` — showing first ${format0(failures.length)}`
                  : ''
              })`}
            >
              <DataTable<AddressFailure>
                data={failures}
                columns={failureColumns}
                getRowId={(f) => String(f.id)}
                config={{ ...PLAIN_TABLE_CONFIG, hideFooter: true }}
              />
            </AccordionItem>
          </Accordion>
        </div>
      )}

      <div className={styles.runLayout}>
        <AddressGroupFilters
          filters={filters}
          executed={executed}
          onChange={onFiltersChange}
          isOpen={filtersOpen}
        />
        <div className={styles.runMain}>
          <DataTable<AddressGroup>
            data={groups}
            columns={columns}
            getRowId={(row) => String(row.id)}
            isLoading={loading}
            searchPlaceholder="Search..."
            leadingToolbarActions={[
              <Button
                key="filters"
                kind="ghost"
                size="lg"
                renderIcon={filtersOpen ? OpenPanelFilledLeft : OpenPanelLeft}
                aria-expanded={filtersOpen}
                onClick={onToggleFilters}
              >
                {filtersOpen ? 'Hide Filters' : 'Show Filters'}
              </Button>,
            ]}
            config={{
              sorting: false,
              globalSearch: true,
              columnFilters: false,
              columnVisibility: false,
              columnResizing: false,
              columnReordering: false,
              columnPinning: false,
              export: false,
              expandAllInHeader: true,
            }}
            scroll={{
              mode: 'paginated',
              serverDriven: true,
              totalCount: total,
            }}
            controlledState={{ pagination, globalFilter: search }}
            onPaginationChange={(s: TableState) =>
              onPaginationChange({ page: s.page, pageSize: s.pageSize })
            }
            onGlobalSearchChange={(s: TableState) =>
              onSearchChange(s.globalFilter)
            }
            renderRowSubComponent={renderVariations}
          />
        </div>
      </div>

      {executions.length > 0 && (
        <section className={styles.executions}>
          <h3 className={styles.executionsHeading}>Executions</h3>
          <DataTable<Run>
            data={executions}
            columns={execColumns}
            getRowId={(row) => String(row.id)}
            config={PLAIN_TABLE_CONFIG}
          />
        </section>
      )}
    </>
  );
}
